import { useQuery } from "@tanstack/react-query";
import { ApiError } from "@njbsoft/api-client";
import { type components } from "@njbsoft/types";
import { apiClient } from "@/lib/auth";
import { useAppStore } from "@/store";

type DocumentScopeMatch = components["schemas"]["DocumentScopeMatch"];
type DocumentScopeSearchResponse = components["schemas"]["DocumentScopeSearchResponse"];

export type { DocumentScopeMatch, DocumentScopeSearchResponse };

// The scope search is the cheap, indexed tree replacement: results shift only on
// upload/delete, so a short stale window keeps the picker snappy while still
// reflecting recent changes when reopened.
const SCOPE_SEARCH_STALE_TIME = 30_000;
const SCOPE_SEARCH_GC_TIME = 5 * 60_000;
// Caps matches PER node_type group; the popover shows a handful per group.
const SCOPE_SEARCH_LIMIT = 8;

/**
 * Query-key factory for the document scope search. Each debounced `q` caches
 * independently under the org prefix so the picker can flip between queries
 * without refetching ones it already has.
 */
export const documentScopeSearchKeys = {
  all: ["document-scope-search"] as const,
  query: (orgId: string, q: string | undefined) =>
    [...documentScopeSearchKeys.all, orgId, q] as const,
};

/**
 * Type-ahead over the searchable document scope axes (facility /
 * device_installation / account), each match carrying a
 * rolled-up `doc_count`. `q` is undefined while the picker is closed (the query
 * stays disabled); when open it carries the debounced search text. An empty
 * (or whitespace-only) box OMITS the `q` param entirely — the OpenAPI validator
 * rejects an empty-string `q` ("empty value is not allowed"), and an omitted
 * `q` returns each group's top entities by doc_count (the picker's open
 * defaults). Threads the active org through the key.
 */
export function useDocumentScopeSearch(q: string | undefined) {
  const orgId = useAppStore((s) => s.activeOrgId);
  return useQuery<DocumentScopeSearchResponse>({
    queryKey: documentScopeSearchKeys.query(orgId ?? "", q),
    queryFn: async () => {
      if (!orgId) throw new Error("No org context");
      const trimmed = q?.trim() ?? "";
      const { data, error, response } = await apiClient.GET(
        "/orgs/{orgId}/documents/scope-search",
        {
          params: {
            path: { orgId },
            // openapi-fetch drops `undefined` query values; sending `q=""` would
            // trip the validator's empty-value rejection (a live 400).
            query: { q: trimmed.length > 0 ? trimmed : undefined, limit: SCOPE_SEARCH_LIMIT },
          },
        },
      );
      if (error) {
        const msg = (error as { error?: string }).error ?? "Failed to search document scopes";
        throw new ApiError(response.status, msg, (error as { code?: string }).code, error);
      }
      return data;
    },
    // Disabled until the picker opens (q becomes defined) and an org exists.
    enabled: Boolean(orgId) && q !== undefined,
    staleTime: SCOPE_SEARCH_STALE_TIME,
    gcTime: SCOPE_SEARCH_GC_TIME,
  });
}
